import "core-js/modules/es6.object.define-property";
import { __decorate } from "tslib";
import { serialize } from "../../Misc/decorators";
import { CameraInputTypes } from "../../Cameras/cameraInputsManager";
import { Matrix, Vector3, Vector2 } from "../../Maths/math.vector";
import { Gamepad } from "../../Gamepads/gamepad";
/**
 * Manage the gamepad inputs to control a free camera.
 * @see http://doc.babylonjs.com/how_to/customizing_camera_inputs
 */

var FreeCameraGamepadInput =
/** @class */
function () {
  function FreeCameraGamepadInput() {
    /**
     * Defines the gamepad rotation sensiblity.
     * This is the threshold from when rotation starts to be accounted for to prevent jittering.
     */
    this.gamepadAngularSensibility = 200;
    /**
     * Defines the gamepad move sensiblity.
     * This is the threshold from when moving starts to be accounted for for to prevent jittering.
     */

    this.gamepadMoveSensibility = 40;
    this._yAxisScale = 1.0;
    this._cameraTransform = Matrix.Identity();
    this._deltaTransform = Vector3.Zero();
    this._vector3 = Vector3.Zero();
    this._vector2 = Vector2.Zero();
  }

  Object.defineProperty(FreeCameraGamepadInput.prototype, "invertYAxis", {
    /**
     * Gets or sets a boolean indicating that Yaxis (for right stick) should be inverted
     */
    get: function get() {
      return this._yAxisScale !== 1.0;
    },
    set: function set(value) {
      this._yAxisScale = value ? -1.0 : 1.0;
    },
    enumerable: true,
    configurable: true
  });
  /**
   * Attach the input controls to a specific dom element to get the input from.
   * @param element Defines the element the controls should be listened from
   * @param noPreventDefault Defines whether event caught by the controls should call preventdefault() (https://developer.mozilla.org/en-US/docs/Web/API/Event/preventDefault)
   */

  FreeCameraGamepadInput.prototype.attachControl = function (element, noPreventDefault) {
    var _this = this;

    var manager = this.camera.getScene().gamepadManager;
    this._onGamepadConnectedObserver = manager.onGamepadConnectedObservable.add(function (gamepad) {
      if (gamepad.type !== Gamepad.POSE_ENABLED) {
        // prioritize XBOX gamepads.
        if (!_this.gamepad || gamepad.type === Gamepad.XBOX) {
          _this.gamepad = gamepad;
        }
      }
    });
    this._onGamepadDisconnectedObserver = manager.onGamepadDisconnectedObservable.add(function (gamepad) {
      if (_this.gamepad === gamepad) {
        _this.gamepad = null;
      }
    });
    this.gamepad = manager.getGamepadByType(Gamepad.XBOX);
  };
  /**
   * Detach the current controls from the specified dom element.
   * @param element Defines the element to stop listening the inputs from
   */


  FreeCameraGamepadInput.prototype.detachControl = function (element) {
    this.camera.getScene().gamepadManager.onGamepadConnectedObservable.remove(this._onGamepadConnectedObserver);
    this.camera.getScene().gamepadManager.onGamepadDisconnectedObservable.remove(this._onGamepadDisconnectedObserver);
    this.gamepad = null;
  };
  /**
   * Update the current camera state depending on the inputs that have been used this frame.
   * This is a dynamically created lambda to avoid the performance penalty of looping for inputs in the render loop.
   */


  FreeCameraGamepadInput.prototype.checkInputs = function () {
    if (this.gamepad && this.gamepad.leftStick) {
      var camera = this.camera;
      var LSValues = this.gamepad.leftStick;
      var normalizedLX = LSValues.x / this.gamepadMoveSensibility;
      var normalizedLY = LSValues.y / this.gamepadMoveSensibility;
      LSValues.x = Math.abs(normalizedLX) > 0.005 ? 0 + normalizedLX : 0;
      LSValues.y = Math.abs(normalizedLY) > 0.005 ? 0 + normalizedLY : 0;
      var RSValues = this.gamepad.rightStick;

      if (RSValues) {
        var normalizedRX = RSValues.x / this.gamepadAngularSensibility;
        var normalizedRY = RSValues.y / this.gamepadAngularSensibility * this._yAxisScale;
        RSValues.x = Math.abs(normalizedRX) > 0.001 ? 0 + normalizedRX : 0;
        RSValues.y = Math.abs(normalizedRY) > 0.001 ? 0 + normalizedRY : 0;
      } else {
        RSValues = {
          x: 0,
          y: 0
        };
      }

      if (!camera.rotationQuaternion) {
        Matrix.RotationYawPitchRollToRef(camera.rotation.y, camera.rotation.x, 0, this._cameraTransform);
      } else {
        camera.rotationQuaternion.toRotationMatrix(this._cameraTransform);
      }

      var speed = camera._computeLocalCameraSpeed() * 50.0;

      this._vector3.copyFromFloats(LSValues.x * speed, 0, -LSValues.y * speed);

      Vector3.TransformCoordinatesToRef(this._vector3, this._cameraTransform, this._deltaTransform);
      camera.cameraDirection.addInPlace(this._deltaTransform);

      this._vector2.copyFromFloats(RSValues.y, RSValues.x);

      camera.cameraRotation.addInPlace(this._vector2);
    }
  };
  /**
   * Gets the class name of the current intput.
   * @returns the class name
   */


  FreeCameraGamepadInput.prototype.getClassName = function () {
    return "FreeCameraGamepadInput";
  };
  /**
   * Get the friendly name associated with the input class.
   * @returns the input friendly name
   */


  FreeCameraGamepadInput.prototype.getSimpleName = function () {
    return "gamepad";
  };

  __decorate([serialize()], FreeCameraGamepadInput.prototype, "gamepadAngularSensibility", void 0);

  __decorate([serialize()], FreeCameraGamepadInput.prototype, "gamepadMoveSensibility", void 0);

  return FreeCameraGamepadInput;
}();

export { FreeCameraGamepadInput };
CameraInputTypes["FreeCameraGamepadInput"] = FreeCameraGamepadInput;